import {Injectable} from '@angular/core';
import {Site, ValidationSummaryType} from '../common/elf-store/add-sites/add-sites.store';
import {FormControl, FormGroup} from '@angular/forms';
import {URL_PROTOCOL_REGEX, validateUrl} from '../common/utils/validators';
import {AdditionalSitesQuery} from '../common/elf-store/add-sites/add-sites.query';
import {finalize, Subscription} from 'rxjs';
import {MySitesApiService} from './api/my-sites-api.service';
import {TrackingService} from './tracking.service';
import {MySitesService} from './my-sites.service';
import {AdditionalSitesManager} from '../common/elf-store/add-sites/add-sites.manager';
import {UserInfoState} from '../common/elf-store/user-info/user-info.type';

@Injectable({
  providedIn: 'root',
})
export class AdditionalSitesService {
  addSiteForm: FormGroup;
  siteList: Site[] = [];
  freeSitesList: string[] = [];
  agencyAccount: UserInfoState['agencyAccount'];

  checkSitesSub: Subscription;

  constructor(
    private additionalSitesManager: AdditionalSitesManager,
    private additionalSitesQuery: AdditionalSitesQuery,
    private sitesApiService: MySitesApiService,
    private sitesService: MySitesService,
    private trackingService: TrackingService,
  ) {
    this.checkSitesSub = this.additionalSitesQuery.siteList$.subscribe((list: Site[]) => (this.siteList = list));
  }

  get websiteControl() {
    return this.addSiteForm.controls.websites;
  }

  get isPaidAgencyAccount() {
    return !this.agencyAccount?.remediationAllowedStates?.length && this.agencyAccount?.enabled;
  }

  get isFreeAgencyAccount() {
    return (
      this.agencyAccount?.remediationAllowedStates?.length === 1 &&
      this.agencyAccount?.remediationAllowedStates?.includes('FREE') &&
      this.agencyAccount?.enabled
    );
  }

  get remediationAllowedFreeAndPaidStates() {
    return (
      this.agencyAccount?.remediationAllowedStates?.includes('FREE') &&
      this.agencyAccount?.remediationAllowedStates?.includes('PAID') &&
      this.agencyAccount?.enabled
    );
  }

  loadFreeSites(): void {
    this.sitesService.getAllFreeWidgetSites().then((sites: string[]) => {
      this.freeSitesList = sites;
    });
  }

  hasError(): boolean {
    return this.addSiteForm.touched && !!this.websiteControl.errors;
  }

  initAddSiteForm() {
    this.addSiteForm = new FormGroup({
      websites: new FormControl(''),
    });
    return this.addSiteForm;
  }

  add(url: string) {
    const urlLink = (url || '').trim();
    const passed = validateUrl(urlLink);
    const brokenUrl = this.hasError() || !urlLink || !passed;
    if (brokenUrl) {
      return false;
    }
    const site: Site = this.createSiteData(urlLink);

    if (!this.alreadyAddedToList(site.host)) {
      this.additionalSitesManager.addSite(site);
      this.checkSitesNotExist([site]);
    } else {
      this.additionalSitesManager.addSite({
        ...site,
        type: ValidationSummaryType.Duplicated,
      });
    }
    return true;
  }

  pasteSites(event: ClipboardEvent | string) {
    let arrayOfSites = [];
    if (typeof event === 'string') {
      arrayOfSites.push(event);
    } else {
      (event as ClipboardEvent).preventDefault();
      const clipboardData = (event as ClipboardEvent).clipboardData || window['clipboardData'];
      const clipboardDataText = clipboardData.getData('text');
      let pastedText;
      if (clipboardDataText.includes('localhost')) {
        pastedText = clipboardDataText.replace(/^http:\/\//, '');
      } else {
        pastedText = clipboardDataText;
      }
      arrayOfSites = pastedText
        .replace(/[|,;\s]+/g, ' ')
        .trim()
        .split(' ');
    }

    if (!arrayOfSites.length) {
      return;
    }

    this.trackingService.trackEvent({
      event: 'paste-site-amount',
      content: `${arrayOfSites.length}`,
    });

    arrayOfSites = arrayOfSites.reduce((accumulator, site) => {
      const passed = validateUrl(site);
      const siteData = this.createSiteData(site, passed);

      if (passed) {
        if (this.alreadyAddedToList(siteData.host)) {
          this.additionalSitesManager.addSite(siteData);
          this.additionalSitesManager.updateValidationSummaryType(siteData.id, ValidationSummaryType.Duplicated);
        } else {
          this.additionalSitesManager.addSite(siteData);
          accumulator.push(siteData);
        }
      } else {
        this.additionalSitesManager.addSite(siteData);
        this.additionalSitesManager.updateValidationSummaryType(siteData.id, ValidationSummaryType.Invalid);
      }

      return accumulator;
    }, []);

    this.checkSitesNotExist(arrayOfSites);
  }

  checkSitesNotExist(newSite: Site[]) {
    this.additionalSitesManager.setLoading(true);

    this.sitesApiService
      .areSitesExist(newSite.map(site => site.url))
      .pipe(
        finalize(() => {
          this.additionalSitesManager.setLoading(false);
          this.additionalSitesManager.markAllSiteLoaded();
        }),
      )
      .subscribe({
        next: ({data}) => {
          data.forEach(site => {
            let type: ValidationSummaryType;

            if (site.exists) {
              type = this.freeSitesList?.includes(this.getHostFromUrl(site.url))
                ? ValidationSummaryType.AlreadyActive
                : ValidationSummaryType.Payed;
            } else {
              type = ValidationSummaryType.Valid;
            }
            const {id} = newSite.find(item => item.url === site.url);
            this.additionalSitesManager.updateValidationSummaryType(id, type);
          });
        },
      });
  }

  remove(id: string) {
    this.additionalSitesManager.removeSite(id);
  }

  onDestroy() {
    this.checkSitesSub?.unsubscribe();
    this.additionalSitesManager.reset();
  }

  private createSiteData(url: string, isUrlValid: boolean = true) {
    return {
      id: this.generateTimestampId(),
      url,
      host: isUrlValid ? this.getHostFromUrl(url) : url,
      loading: true,
    };
  }

  private generateTimestampId() {
    const timestamp = new Date().getTime();
    const random = Math.floor(Math.random() * 900) + 100;
    return `${timestamp}${random}`;
  }

  private getHostFromUrl(url: string) {
    if (!URL_PROTOCOL_REGEX.test(url)) {
      url = `http://${url}`;
    }

    url = new URL(url).host;

    const urlSplitArray = url.split('.');
    if (urlSplitArray[0] === 'www') {
      urlSplitArray.shift();
    }
    url = urlSplitArray.join('.');

    return url;
  }

  private alreadyAddedToList(host: string) {
    return !!this.siteList?.find(site => site.host === host);
  }
}
